/**
 * Reading the controllers, in the shape the arm already understands.
 *
 * Field for field this is what `src/pick_place/teleop/static/index.html` sends and what
 * `pick_place.teleop.adamo.operator` documents, because the robot's side of that contract is
 * tested and in use. The button indices are the xr-standard Quest Touch layout.
 */
import type { HandFrame } from "../adamo/types";

export type Ray = { origin: DOMPointReadOnly; direction: [number, number, number] };

const pressed = (gamepad: Gamepad | undefined, index: number): boolean =>
  Boolean(gamepad && gamepad.buttons.length > index && gamepad.buttons[index].pressed);

export function readHand(source: XRInputSource, frame: XRFrame, refSpace: XRReferenceSpace): HandFrame | null {
  if (!source.gripSpace || (source.handedness !== "left" && source.handedness !== "right")) return null;
  const pose = frame.getPose(source.gripSpace, refSpace);
  const gamepad = source.gamepad ?? undefined;
  const axes = gamepad?.axes ?? [];
  const p = pose?.transform.position;
  const o = pose?.transform.orientation;
  return {
    stamp: 0,
    operator_id: "",
    pos: p ? [p.x, p.y, p.z] : null,
    quat: o ? [o.x, o.y, o.z, o.w] : null,
    head_quat: null,
    // position is dead-reckoned when the controller is out of the headset's view: it drifts,
    // then snaps, and the arm's clutch drops on it rather than following the snap
    emulated: Boolean(pose && (pose as XRPose).emulatedPosition),
    trigger: gamepad && gamepad.buttons.length > 0 ? gamepad.buttons[0].value : 0,
    squeeze: pressed(gamepad, 1),
    stick_press: pressed(gamepad, 3),
    a: pressed(gamepad, 4),
    b: pressed(gamepad, 5),
    stick: [axes[2] ?? 0, axes[3] ?? 0],
  };
}

/** Where the controller is pointing, for the menu's cursor. */
export function readRay(source: XRInputSource, frame: XRFrame, refSpace: XRReferenceSpace): Ray | null {
  const pose = frame.getPose(source.targetRaySpace, refSpace);
  if (!pose) return null;
  const { position, orientation: q } = pose.transform;
  // rotate (0, 0, -1) by the pose's quaternion
  const x = q.x, y = q.y, z = q.z, w = q.w;
  return {
    origin: position,
    direction: [-2 * (x * z + w * y), -2 * (y * z - w * x), -(1 - 2 * (x * x + y * y))],
  };
}

export function headQuaternion(frame: XRFrame, refSpace: XRReferenceSpace): [number, number, number, number] | null {
  const pose = frame.getViewerPose(refSpace);
  if (!pose) return null;
  const { x, y, z, w } = pose.transform.orientation;
  return [x, y, z, w];
}

export function buzz(source: XRInputSource, intensity: number, ms: number): void {
  const actuators = (source.gamepad as Gamepad & { hapticActuators?: { pulse?: (i: number, d: number) => void }[] })
    ?.hapticActuators;
  actuators?.[0]?.pulse?.(Math.max(0, Math.min(1, intensity)), ms);
}
